import React, { useRef } from 'react';
import { useModal } from 'components/v2/Modal/useModal';
import { refreshReportItem } from '../../reports/hooks/use-report-item';
import { Props } from '../forms/IntelligenceFieldsForm';
import {
  FormMethods,
  MultipleDirectoryIntelligenceFieldsForm,
} from '../forms/MultipleDirectoryIntelligenceFieldsForm';
import { Dir } from '../../dirs';
import useSnackbars from '../../../components/v2/Snackbar/use-snackbars';
import { useTranslation } from 'utils/translation';
import { createModalFormFooter } from '../../../components/v2/Modal/createModalFormFooter';
import { ModalConfig } from '../../../components/v2/Modal/ModalContext';
import Button, { ButtonGroup } from '../../../components/v2/Button/Button';
import { useModalContent } from '../../../components/v2/Modal/useModalContent';

type Options = Pick<Props, 'onSuccess'>;

const MULTIPLE_DIRECTORY_INTELLIGENCE_FORM_ID =
  'intelligence-layer-multiple-directory-fields-form';

const CONFIRMATION_FORM_ID =
  'intelligence-layer-multiple-directory-fields-form-confirmation';

const MultipleDirectoryIntelligenceModalFooter = createModalFormFooter(
  MULTIPLE_DIRECTORY_INTELLIGENCE_FORM_ID,
);

const ConfirmationModalFooter = ({ onCancel }: { onCancel(): void }) => {
  const { isBusy } = useModalContent();
  const { t } = useTranslation();

  return (
    <ButtonGroup align="right">
      <Button variant="cancel" onClick={onCancel} disabled={isBusy}>
        {t('intelligence_layer_bulk_confirm_adjust')}
      </Button>
      <Button
        isBusy={isBusy}
        type="submit"
        form={CONFIRMATION_FORM_ID}
        size="small"
      >
        {t('intelligence_layer_bulk_confirm_confirm')}
      </Button>
    </ButtonGroup>
  );
};

function MultipleDirectoryIntelligenceModalContent({
  close,
  update,
  dirs,
  onSuccess,
}: {
  close: () => void;
  update: (config: ModalConfig) => void;
  dirs: Dir[];
  onSuccess?: () => void;
}) {
  const snackbars = useSnackbars({ cleanupOnUnmount: false });
  const { t } = useTranslation();
  const methodRef = useRef<FormMethods>(null);

  return (
    <MultipleDirectoryIntelligenceFieldsForm
      methods={methodRef}
      formId={MULTIPLE_DIRECTORY_INTELLIGENCE_FORM_ID}
      confirmationFormId={CONFIRMATION_FORM_ID}
      dirs={dirs}
      onConfirmRequest={() => {
        update({
          title: t('intelligence_layer_bulk_confirm_title'),
          footer: () => (
            <ConfirmationModalFooter
              onCancel={() => {
                methodRef.current?.resetPendingValues();
                update({
                  title: t('intelligence_layer_bulk_update_title'),
                  footer: MultipleDirectoryIntelligenceModalFooter,
                });
              }}
            />
          ),
        });
      }}
      onSuccess={() => {
        close();
        onSuccess?.();
        dirs.forEach((d) => {
          refreshReportItem(d.id);
        });
        snackbars.add({
          variant: 'success',
          message: t('Successfully updated'),
        });
      }}
    />
  );
}

export default function useMultipleDirectoryFieldsForm({
  onSuccess,
}: Options = {}) {
  const modal = useModal();
  const { t } = useTranslation();

  return (dirs: Dir[]) => {
    if (!dirs.length) {
      return;
    }

    modal.open(
      ({ close, update }) => (
        <MultipleDirectoryIntelligenceModalContent
          update={update}
          close={close}
          dirs={dirs}
          onSuccess={onSuccess}
        />
      ),
      {
        title: t('intelligence_layer_bulk_update_title'),
        footer: MultipleDirectoryIntelligenceModalFooter,
      },
    );
  };
}
